import type { RouterOutputs } from 'shared/api'
import type { TranslationKey } from 'shared/lib/i18n'

/** The «Оповещения» screen's shapes and rules (ticket 46), apart from its components. */

export type NotificationTargets = RouterOutputs['notifications']['Targets']
export type NotificationTarget = NotificationTargets['targets'][number]
export type NotificationGroup = NonNullable<NotificationTarget['group']>
export type NoticeLogRow = RouterOutputs['notifications']['Log'][number]
export type BindingCode = RouterOutputs['notifications']['StartBinding']

/** The words a refusal of a notifications call is read in, by its code. */
export function notificationFailureKey(error: unknown): TranslationKey {
	const code = (error as { data?: { code?: string } } | null)?.data?.code
	switch (code) {
		case 'NOT_FOUND':
			return 'notifications.failNotBound'
		case 'CONFLICT':
			return 'notifications.failBotRemoved'
		case 'FORBIDDEN':
			return 'notifications.failProd'
		case 'BAD_REQUEST':
			return 'notifications.failChatId'
		default:
			return 'notifications.failed'
	}
}

/** Seconds left of a binding code at `now` (ms); 0 once it has expired. */
export function secondsLeft(expiresAt: Date | string, now: number): number {
	return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 1000))
}

/** `14:05` — minutes and seconds of a countdown. */
export function countdown(seconds: number): string {
	const m = Math.floor(seconds / 60)
	const s = seconds % 60
	return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Whether the binding the admin started at `startedAt` has happened: the target's
 * group was bound at or after it (a group bound before is the one being replaced).
 */
export function boundSince(group: NotificationGroup | null, startedAt: number): boolean {
	return !!group && group.state === 'active' && new Date(group.boundAt).getTime() >= startedAt
}

/** Does a target match what the admin typed in the filter (its id, its group's title)? */
export function matchesFilter(target: NotificationTarget, filter: string): boolean {
	const q = filter.trim().toLowerCase()
	if (!q) return true
	return [target.locationCode ?? '', target.group?.chatTitle ?? ''].some((s) =>
		s.toLowerCase().includes(q),
	)
}

/** A Telegram chat id as the server takes it: a whole number, a group's negative. */
export function isChatId(value: string): boolean {
	return /^-?\d+$/.test(value.trim())
}

/**
 * Whether a target is listed: support, the cashier and the hub always, and every
 * Point with a group; a Point without one only when the admin looks for it (the
 * filter) or asks for all of them — the network has a hundred Points.
 */
export function shownTarget(target: NotificationTarget, filter: string, allPoints: boolean): boolean {
	if (!matchesFilter(target, filter)) return false
	if (target.kind !== 'point' || target.group || filter.trim() || allPoints) return true
	return false
}

/** The Points the list hides now. */
export function hiddenPoints(targets: NotificationTarget[], filter: string, allPoints: boolean): NotificationTarget[] {
	return targets.filter((t) => t.kind === 'point' && matchesFilter(t, filter) && !shownTarget(t, filter, allPoints))
}
